/**
 * Carregador único do script reCAPTCHA Enterprise.
 *
 * O script é injetado uma vez por carregamento de página e, em caso de sucesso, NUNCA é
 * removido. Isso é requisito do Account Defender: a coleta passiva de telemetria
 * horizontal precisa estar ativa em todas as páginas da jornada do usuário — inclusive
 * nas logadas — para que ele consiga distinguir um humano navegando de um acesso
 * isolado vindo de um IP compartilhado (NAT/VPN corporativa).
 *
 * Por isso a lib react-google-recaptcha-v3 foi abandonada (e sua dependência removida): o
 * cleanup do `GoogleReCaptchaProvider` dela remove o script, remove a badge e faz
 * `window.___grecaptcha_cfg = undefined` no unmount, derrubando o runtime inteiro a
 * cada navegação para fora de uma rota protegida.
 *
 * Carregar o script não gera assessment nem custo: a cobrança do reCAPTCHA é por
 * requisição a `CreateAssessment`, feita pelo backend a partir de um token de
 * `execute()`.
 */

export type RecaptchaEnterpriseApi = NonNullable<NonNullable<Window['grecaptcha']>['enterprise']>

const SCRIPT_ID = 'google-recaptcha-enterprise'
const SCRIPT_SRC = 'https://www.google.com/recaptcha/enterprise.js'

/**
 * Teto para o ciclo completo de carregamento (download + `ready()`). Mais generoso que
 * o timeout de `execute()` porque aqui há download de script envolvido, que numa rede
 * ruim é legitimamente lento.
 */
const LOAD_TIMEOUT_MS = 15_000

const MISSING_SITE_KEY_MESSAGE = '[reCAPTCHA] Site key não configurada'
const SCRIPT_LOAD_FAILED_MESSAGE = '[reCAPTCHA] Falha ao carregar o script Enterprise'
const SCRIPT_UNAVAILABLE_MESSAGE = '[reCAPTCHA] Script carregado sem expor grecaptcha.enterprise'
const LOAD_TIMEOUT_MESSAGE = '[reCAPTCHA] Timeout ao carregar o script Enterprise'

let loadPromise: Promise<RecaptchaEnterpriseApi> | null = null

/**
 * Invalida o memo de um carregamento bem-sucedido — a falha já se auto-invalida, o sucesso
 * fica em cache para sempre sem verificação de vida do runtime.
 *
 * Remove a tag junto: sucesso não a remove, e deixá-la faria o próximo load cair no branch de
 * reuso registrando listeners de `load`/`error` num elemento cujos eventos já dispararam —
 * resolveria só pelo timeout de {@link LOAD_TIMEOUT_MS}.
 *
 * Só recupera quando os globais do reCAPTCHA desapareceram (remoção por terceiros). Com
 * runtime degradado mas presente, o próximo load reusa `window.grecaptcha.enterprise`; zerar
 * `___grecaptcha_cfg` à força derrubaria a telemetria do Account Defender.
 */
export const resetRecaptchaEnterpriseLoader = (): void => {
  loadPromise = null
  document.getElementById(SCRIPT_ID)?.remove()
}

/**
 * Carrega o script Enterprise sob demanda e memoiza o resultado, de modo que qualquer
 * número de chamadores compartilhe uma única instância do runtime.
 *
 * Uma falha NÃO fica em cache: a próxima chamada pode reinjetar o script.
 */
export const loadRecaptchaEnterprise = (siteKey: string): Promise<RecaptchaEnterpriseApi> => {
  if (loadPromise) {
    return loadPromise
  }

  const pending = new Promise<RecaptchaEnterpriseApi>((resolve, reject) => {
    if (!siteKey) {
      reject(new Error(MISSING_SITE_KEY_MESSAGE))
      return
    }

    let settled = false

    /**
     * Toda falha precisa remover o `<script>` do DOM. Deixá-lo lá faria a próxima
     * chamada cair no branch de reuso e registrar listeners de `load`/`error` num
     * elemento cujos eventos já dispararam: a promise nunca resolveria nem rejeitaria,
     * o memo ficaria preso nela e as falhas seguintes seriam silenciosas.
     */
    const fail = (message: string) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      document.getElementById(SCRIPT_ID)?.remove()
      reject(new Error(message))
    }

    const succeed = (api: RecaptchaEnterpriseApi) => {
      if (settled) return
      settled = true
      clearTimeout(timeoutId)
      resolve(api)
    }

    // `ready()` pode nunca invocar o callback — proxy corporativo que devolve um
    // interstitial no lugar do script, por exemplo. Sem este teto a promise ficaria
    // pendente para sempre, sem nem chegar ao `.catch` que reseta o memo.
    // Declarado após `fail`/`succeed` porque ambos o leem no `clearTimeout`; nenhum dos
    // dois é chamado antes desta linha.
    const timeoutId = setTimeout(() => fail(LOAD_TIMEOUT_MESSAGE), LOAD_TIMEOUT_MS)

    /**
     * `ready()` só dispara quando o runtime terminou de inicializar; resolver antes
     * dele entregaria uma API cujo `execute()` ainda falha.
     */
    const resolveWhenReady = () => {
      const api = window.grecaptcha?.enterprise

      if (!api) {
        fail(SCRIPT_UNAVAILABLE_MESSAGE)
        return
      }

      api.ready(() => succeed(api))
    }

    // Runtime já inicializado: o script pode ter executado DEPOIS de um fail por timeout,
    // que remove a tag mas não desfaz os globais. Reaproveita em vez de injetar um segundo
    // enterprise.js sobre um `___grecaptcha_cfg` já definido.
    if (window.grecaptcha?.enterprise) {
      resolveWhenReady()
      return
    }

    // Script já no DOM sem runtime pronto (HMR remontando o módulo, por exemplo):
    // reaproveita em vez de injetar um segundo, que reinicializaria o runtime.
    const existingScript = document.getElementById(SCRIPT_ID)
    if (existingScript) {
      existingScript.addEventListener('load', resolveWhenReady, { once: true })
      existingScript.addEventListener('error', () => fail(SCRIPT_LOAD_FAILED_MESSAGE), {
        once: true
      })
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = `${SCRIPT_SRC}?render=${encodeURIComponent(siteKey)}`
    script.async = true
    script.onload = resolveWhenReady
    script.onerror = () => fail(SCRIPT_LOAD_FAILED_MESSAGE)

    document.head.appendChild(script)
  })

  loadPromise = pending

  pending.catch(() => {
    if (loadPromise === pending) {
      loadPromise = null
    }
  })

  return pending
}
